import {useCallback, useMemo, useState} from 'react';
import {z} from 'zod';
import {ExamModule, QuestionStatus} from '@cp/model';
import {useSearchParamsHandler} from '../../hooks/helper/useSearchParamsHandler';
import QuestionFilters from './QuestionFilters';
import {QuestionFiltersSettings} from './QuestionFiltersSettings';

const questionFiltersSchema = z.object({
  moduleId: z.string().optional(),
  topic: z.string().optional(),
  difficulty: z.number().int().min(1).max(5).optional(),
  status: z.nativeEnum(QuestionStatus).optional(),
});

export interface UseQuestionFiltersOptions {
  examModules: ExamModule[];
  modulesLoading: boolean;
  moduleLabel?: string;
  totalItems?: number;
  disabled?: boolean;
  statusAllowAll?: boolean;
  statusAvailableStatuses?: QuestionStatus[];
  statusDisabled?: boolean;
  statusMinWidth?: number;
  defaultExpanded?: boolean;
  /** Enable URL search params persistence for filters */
  useUrlParams?: boolean;
}

export interface UseQuestionFiltersReturn {
  filters: QuestionFiltersSettings;
  setFilters: React.Dispatch<React.SetStateAction<QuestionFiltersSettings>>;
  QuestionFiltersComponent: React.ComponentType<{children?: React.ReactNode}>;
  selectedModule: ExamModule | undefined;
  availableTopics: ExamModule['topics'];
  filtersExpanded: boolean;
  setFiltersExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function useQuestionFilters(options: UseQuestionFiltersOptions): UseQuestionFiltersReturn {
  const {
    examModules,
    modulesLoading,
    moduleLabel,
    totalItems,
    disabled = false,
    statusAllowAll = true,
    statusAvailableStatuses,
    statusDisabled = false,
    statusMinWidth,
    defaultExpanded = false,
    useUrlParams = false,
  } = options;

  // Use URL params or local state based on option
  const urlParamsHandler = useSearchParamsHandler<QuestionFiltersSettings>({
    schema: questionFiltersSchema,
    defaultValues: {
      moduleId: '',
      topic: '',
      difficulty: undefined,
      status: undefined,
    },
  });

  const [localFilters, setLocalFilters] = useState<QuestionFiltersSettings>({});
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(defaultExpanded);

  // Use URL params or local state based on option
  const {searchParams, setSearchParams} = urlParamsHandler;
  const filters = useUrlParams ? searchParams : localFilters;

  const setFilters = useCallback<React.Dispatch<React.SetStateAction<QuestionFiltersSettings>>>(
    (update) => {
      if (!useUrlParams) {
        setLocalFilters(update);
        return;
      }

      const newFilters = typeof update === 'function' ? update(searchParams) : update;
      setSearchParams(newFilters);
    },
    [searchParams, setSearchParams, useUrlParams],
  );

  const selectedModule = useMemo(
    () => examModules.find((module) => module.id === filters.moduleId),
    [examModules, filters.moduleId],
  );

  const availableTopics = useMemo(() => selectedModule?.topics || [], [selectedModule]);

  const handleModuleChange = useCallback(
    (moduleId?: string) => {
      setFilters((prev) => ({...prev, moduleId, topic: undefined}));
    },
    [setFilters],
  );

  const handleTopicChange = useCallback(
    (topic?: string) => {
      setFilters((prev) => ({...prev, topic}));
    },
    [setFilters],
  );

  const handleDifficultyChange = useCallback(
    (difficulty?: number) => {
      setFilters((prev) => ({...prev, difficulty}));
    },
    [setFilters],
  );

  const handleStatusChange = useCallback(
    (status?: QuestionStatus) => {
      setFilters((prev) => ({...prev, status}));
    },
    [setFilters],
  );

  const handleAccordionChange = useCallback((_event: React.SyntheticEvent, isExpanded: boolean) => {
    setFiltersExpanded(isExpanded);
  }, []);

  const QuestionFiltersComponent = useMemo(
    () =>
      function QuestionFiltersComponent({children}: {children?: React.ReactNode}) {
        return (
          <QuestionFilters
            dropdownModule={{
              moduleId: filters.moduleId,
              onChange: handleModuleChange,
              examModules: examModules,
              disabled: modulesLoading || disabled,
              label: moduleLabel,
            }}
            dropdownTopic={{
              value: filters.topic,
              onChange: handleTopicChange,
              availableTopics: availableTopics,
              allowAll: true,
              disabled: modulesLoading || disabled,
            }}
            dropdownDifficulty={{
              difficulty: filters.difficulty,
              onChange: handleDifficultyChange,
            }}
            dropdownQuestionStatus={{
              status: filters.status,
              onChange: handleStatusChange,
              allowAll: statusAllowAll,
              availableStatuses: statusAvailableStatuses,
              disabled: statusDisabled || modulesLoading || disabled,
              minWidth: statusMinWidth,
            }}
            totalItems={totalItems}
            expanded={filtersExpanded}
            handleExpand={handleAccordionChange}
          >
            {children}
          </QuestionFilters>
        );
      },
    [
      filters,
      handleModuleChange,
      handleTopicChange,
      handleDifficultyChange,
      handleStatusChange,
      examModules,
      modulesLoading,
      disabled,
      moduleLabel,
      availableTopics,
      statusAllowAll,
      statusAvailableStatuses,
      statusDisabled,
      statusMinWidth,
      totalItems,
      filtersExpanded,
      handleAccordionChange,
    ],
  );

  return {
    filters,
    setFilters,
    QuestionFiltersComponent,
    selectedModule,
    availableTopics,
    filtersExpanded,
    setFiltersExpanded,
  };
}
